import { useState, useEffect } from "react";
import styled from "styled-components";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LightboxImage = styled.img`
  max-height: 85vh;
  max-width: 80vw;
  object-fit: contain;
  user-select: none;
  pointer-events: none;

  @media (max-width: 768px) {
    max-width: 95vw;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 24px;
  right: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10001;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const NavButton = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.$side === "left" ? "left: 24px;" : "right: 24px;")}
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10001;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: #c9a86c;
    color: #c9a86c;
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    ${(props) => (props.$side === "left" ? "left: 8px;" : "right: 8px;")}
  }
`;

const Counter = styled.div`
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  color: #c9a86c;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 2px;
  z-index: 10001;
`;

const Lightbox = ({ images, initialIndex, onClose }) => {
  const [current, setCurrent] = useState(initialIndex ?? 0);

  const prev = (e) => {
    e?.stopPropagation();
    setCurrent((i) => (i - 1 + images.length) % images.length);
  };

  const next = (e) => {
    e?.stopPropagation();
    setCurrent((i) => (i + 1) % images.length);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  return (
    <Overlay onClick={onClose}>
      <CloseButton onClick={onClose}>
        <X size={22} />
      </CloseButton>

      {images.length > 1 && (
        <NavButton $side="left" onClick={prev}>
          <ChevronLeft size={32} />
        </NavButton>
      )}

      <ImageWrapper onClick={(e) => e.stopPropagation()}>
        <LightboxImage src={images[current]} alt={`Image ${current + 1}`} />
      </ImageWrapper>

      {images.length > 1 && (
        <>
          <NavButton $side="right" onClick={next}>
            <ChevronRight size={32} />
          </NavButton>
          <Counter>
            {current + 1} / {images.length}
          </Counter>
        </>
      )}
    </Overlay>
  );
};

export default Lightbox;
